import { Playbook } from '@/types/playbook/playbook';
import { Bot, CheckCircle, Lightbulb, Mail, User, Zap } from 'lucide-react';

export const proTipNudge: Playbook = {
  meta: {
    id: 'pro-tip-nudge',
    slug: 'pro-tip-nudge',
    categoryId: 'onboard-educate',
    name: 'The "Pro-Tip" Nudge',
    tagline: 'Share power-user tips with regulars.',
    description: 'Detect experienced users and share advanced tips to help them get even more value from your product.',
    strategy: 'Detect experienced users and share advanced tips to help them get even more value from your product.',
    benefits: [
      'Increase product stickiness',
      'Unlock advanced features',
      'Improve power user satisfaction',
      'Reduce feature discovery time'
    ],
    targetAudience: ['SaaS platforms', 'Productivity tools', 'Professional software'],
    estimatedSetupTime: '30 minutes',
    difficulty: 'advanced',
    tags: ['power-users', 'advanced-tips', 'feature-discovery', 'user-engagement'],
    isFeatured: false
  },
  seo: {
    metaTitle: 'Pro-Tip Nudge Playbook - Advanced User Education',
    metaDescription: 'Engage power users with Genie-delivered advanced tips and shortcuts. Increase feature adoption and user satisfaction.',
    keywords: ['power user tips', 'advanced features', 'user education', 'feature discovery', 'product mastery']
  },
  demoConfig: {
    triggerType: 'delayed',
    delay: 3000,
    message: "💡 Pro tip: I noticed you use this feature often. Did you know you can save 50% of your time by using keyboard shortcuts? Want me to show you?",
    position: 'bottom-right'
  },
  demoScript: [
    { speaker: 'agent', message: "💡 I see you're a power user! Want to learn a time-saving trick?" },
    { speaker: 'user', message: "Sure, what is it?" },
    { speaker: 'agent', message: "You can use Cmd+K to quick-search anything. Try it now!" },
    { speaker: 'user', message: "Wow, that's fast!" },
    { speaker: 'agent', message: "There's more! Want me to email you our power user guide?" }
  ],
  templateConfig: {
    firstMessage: "💡 Pro tip: Based on your usage, I have some advanced tips that could help you.",
    systemPrompt: "You are a product-expert Genie sharing advanced tips with experienced users. Be helpful and share genuinely useful shortcuts and hidden features.",
    tools: ['usage_analytics', 'feature_guides', 'email_capture']
  },
  flowSteps: [
    {
      type: 'user',
      icon: User,
      genieIcon: 'user',
      label: 'Experienced user detected',
      description: 'System identifies user with high engagement and feature usage patterns'
    },
    {
      type: 'genie',
      icon: Lightbulb,
      genieIcon: 'genie-lamp',
      label: 'Analyzes usage patterns',
      description: 'Genie reviews user behavior to identify opportunities for efficiency improvements'
    },
    {
      type: 'action',
      icon: Bot,
      genieIcon: 'settings',
      label: 'Delivers contextual tip',
      description: 'Shares relevant advanced feature or shortcut based on current user activity'
    },
    {
      type: 'action',
      icon: Zap,
      genieIcon: 'settings',
      label: 'Demonstrates value instantly',
      description: 'Shows immediate benefit and time savings from the suggested improvement'
    },
    {
      type: 'action',
      icon: Mail,
      genieIcon: 'mail',
      label: 'Offers additional resources',
      description: 'Provides access to power user guides or advanced training materials'
    },
    {
      type: 'result',
      icon: CheckCircle,
      genieIcon: 'trending-up',
      label: 'Enhanced user proficiency',
      description: 'User becomes more efficient and discovers valuable advanced capabilities'
    }
  ],
  metrics: {
    conversionRate: '37%',
    responseTime: '<2 seconds',
    satisfactionScore: '4.8/5',
    costSavings: '$35k/month',
    timeToValue: 'Immediate'
  },
  testimonials: [
    {
      companyName: 'ProductivityMax',
      authorName: 'David Chen',
      authorTitle: 'Head of Customer Success',
      quote: 'Pro-tip nudges increased feature adoption by 45% among our most engaged users. They love discovering new efficiency hacks.',
      metrics: '45% increase in advanced feature usage'
    }
  ],
  resources: [
    {
      type: 'guide',
      title: 'Power User Analytics Setup',
      description: 'How to identify and segment experienced users for targeted tips',
      url: '/docs/power-user-analytics'
    },
    {
      type: 'template',
      title: 'Advanced Feature Tips Library',
      description: 'Curated collection of power user tips for different product areas',
      url: '/templates/power-user-tips'
    }
  ]
};